import React from 'react';

const AlertDetailModal = ({ type, vehicles, onClose, navigate }) => {
    const titles = {
        insurance: '🛡️ Insurance Expiry Details',
        technical: '🔧 Technical Check Details',
        oil: '🛢️ Oil Change Alerts'
    };

    const getDaysText = (date) => {
        if (!date) return 'No date set';
        const now = new Date();
        const expiry = new Date(date);
        const diffDays = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
        if (diffDays < 0) return `${Math.abs(diffDays)} days overdue`;
        if (diffDays === 0) return 'Expires today';
        return `${diffDays} days left`;
    };

    const formatDate = (date) => {
        if (!date) return 'Not set';
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric'
        });
    };

    const getStatusColor = (date) => {
        if (!date) return 'text-slate-400';
        const now = new Date();
        const expiry = new Date(date);
        const diffDays = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
        if (diffDays < 0) return 'text-red-400';
        if (diffDays <= 7) return 'text-orange-400';
        if (diffDays <= 30) return 'text-yellow-400';
        return 'text-green-400';
    };

    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div
                className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden shadow-2xl"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-5 border-b border-slate-700 bg-slate-800/50">
                    <h2 className="text-xl font-bold text-white">{titles[type]}</h2>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-700 hover:bg-slate-600 text-slate-400 hover:text-white transition-colors"
                    >
                        ✕
                    </button>
                </div>

                {/* Content */}
                <div className="p-5 overflow-y-auto max-h-[60vh] space-y-4">
                    {vehicles.length === 0 ? (
                        <p className="text-slate-400 text-center py-8">No vehicles found</p>
                    ) : (
                        vehicles.map(v => (
                            <div
                                key={v._id}
                                className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 hover:bg-slate-800 transition-colors"
                            >
                                <div className="flex items-start justify-between">
                                    <div className="flex items-center gap-4">
                                        <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center text-2xl">
                                            🚗
                                        </div>
                                        <div>
                                            <p className="font-bold text-white text-lg">{v.plateNumber}</p>
                                            <p className="text-slate-400">{v.brand} {v.model} {v.year}</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => { onClose(); navigate(`/vehicles/${v._id}`); }}
                                        className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded-lg transition-colors"
                                    >
                                        View →
                                    </button>
                                </div>

                                <div className="mt-4 pt-4 border-t border-slate-700/50">
                                    {type === 'insurance' && (
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <p className="text-slate-500 text-xs uppercase tracking-wide mb-1">Insurance Company</p>
                                                <p className="text-white font-medium flex items-center gap-2">
                                                    🏢 {v.insurance?.company || 'Not specified'}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-slate-500 text-xs uppercase tracking-wide mb-1">Expiry Date</p>
                                                <p className={`font-medium ${getStatusColor(v.insurance?.expiry)}`}>
                                                    📅 {formatDate(v.insurance?.expiry)}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-slate-500 text-xs uppercase tracking-wide mb-1">Status</p>
                                                <p className={`font-semibold ${getStatusColor(v.insurance?.expiry)}`}>
                                                    ⏰ {getDaysText(v.insurance?.expiry)}
                                                </p>
                                            </div>
                                            <div className="flex items-end">
                                                <a
                                                    href={`/vehicles/${v._id}?tab=insurance`}
                                                    onClick={(e) => { e.preventDefault(); onClose(); navigate(`/vehicles/${v._id}`); }}
                                                    className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500 text-white text-sm font-medium rounded-lg transition-all flex items-center gap-2"
                                                >
                                                    🔄 Renew Insurance
                                                </a>
                                            </div>
                                        </div>
                                    )}

                                    {type === 'technical' && (
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <p className="text-slate-500 text-xs uppercase tracking-wide mb-1">Inspection Center</p>
                                                <p className="text-white font-medium flex items-center gap-2">
                                                    🏭 {v.technicalCheck?.company || 'Not specified'}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-slate-500 text-xs uppercase tracking-wide mb-1">Expiry Date</p>
                                                <p className={`font-medium ${getStatusColor(v.technicalCheck?.expiry)}`}>
                                                    📅 {formatDate(v.technicalCheck?.expiry)}
                                                </p>
                                            </div>
                                            <div className="col-span-2">
                                                <p className="text-slate-500 text-xs uppercase tracking-wide mb-1">Status</p>
                                                <p className={`font-semibold ${getStatusColor(v.technicalCheck?.expiry)}`}>
                                                    ⏰ {getDaysText(v.technicalCheck?.expiry)}
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    {type === 'oil' && (
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <p className="text-slate-500 text-xs uppercase tracking-wide mb-1">Current Odometer</p>
                                                <p className="text-white font-medium">
                                                    📍 {Number(v.odometerReading || 0).toLocaleString()} {v.distanceUnit || 'km'}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-slate-500 text-xs uppercase tracking-wide mb-1">Next Oil Change</p>
                                                <p className="text-yellow-400 font-medium">
                                                    🎯 {Number(v.nextOilChangeOdometer || 0).toLocaleString()} {v.distanceUnit || 'km'}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-slate-500 text-xs uppercase tracking-wide mb-1">Remaining</p>
                                                {(() => {
                                                    const diff = (v.nextOilChangeOdometer || 0) - (v.odometerReading || 0);
                                                    const isOverdue = diff < 0;
                                                    return (
                                                        <p className={`font-semibold ${isOverdue ? 'text-red-400' : diff <= 500 ? 'text-orange-400' : 'text-green-400'}`}>
                                                            {isOverdue ? '⚠️' : '⚡'} {isOverdue ? '' : '+'}{diff.toLocaleString()} {v.distanceUnit || 'km'}
                                                            {isOverdue && <span className="ml-2 text-xs">(OVERDUE)</span>}
                                                        </p>
                                                    );
                                                })()}
                                            </div>
                                            <div>
                                                <p className="text-slate-500 text-xs uppercase tracking-wide mb-1">Interval</p>
                                                <p className="text-slate-300 font-medium">
                                                    🔄 Every {(v.oilChangeIntervalKm || 5000).toLocaleString()} {v.distanceUnit || 'km'}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-slate-700 bg-slate-800/30">
                    <p className="text-slate-500 text-sm text-center">
                        {vehicles.length} vehicle{vehicles.length !== 1 ? 's' : ''} {type === 'oil' ? 'need attention' : 'with expired or expiring ' + type}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default AlertDetailModal;
